import { useEffect, useState } from 'react';
import Tooltip from '../ui/tooltips/Tooltip';
import style from '../ui/modals/modals.module.scss';
import { testMap } from '../../services/importService';
import Toast from '../ui/toasts/Toast';
import { getText } from '../../i18n';

const ImportMapTest = ({ data, errorMessage, warningMessage }) => {
    const [fieldNamesWithValues, setFieldNamesWithValues] = useState([]);
    const [evaluationResult, setEvaluationResult] = useState(null);

    useEffect(() => {
        const leftExpressionFieldNams = getFieldNames(
            data.evaluations[0].leftExpression,
        );
        const rightExpressionFieldNames = getFieldNames(
            data.evaluations[0].rightExpression,
        );
        const valueFieldNames = getFieldNames(data.value);
        const errorFieldName = getFieldNames(errorMessage);
        const warningFieldNames = getFieldNames(warningMessage);

        // make this a case insensitive unique list of field names
        const allFieldNames = [
            ...leftExpressionFieldNams,
            ...rightExpressionFieldNames,
            ...valueFieldNames,
            ...errorFieldName,
            ...warningFieldNames,
        ];

        const uniqueFieldNames = [
            ...new Set(allFieldNames.map(name => toProperCase(name))),
        ];
        const uniqueFieldNamesWithValues = uniqueFieldNames.map(name => ({
            name: name,
            value: '',
        }));
        setFieldNamesWithValues(uniqueFieldNamesWithValues);
    }, []);

    function toProperCase(str) {
        return str.toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
    }

    const testMapping = async () => {
        const trimmedFieldNamesWithValues = fieldNamesWithValues.map(field => ({
            name: field.name,
            value: field.value.trim(),
        }));

        // submit mapping test request
        var payload = {
            mapFunction: data,
            mapFields: trimmedFieldNamesWithValues,
            errorMessage,
            warningMessage,
        };

        try {
            const response = await testMap(payload);
            setEvaluationResult(response.data);
        } catch (error) {
            console.error('Error testing map:', error);
            Toast.error(getText('msg_57cecc79497c'));

            return;
        }
    };

    // get field name out of value of functions
    const getFieldNames = value => {
        if (!value || typeof value !== 'string') return [];

        const regex = /ValueOf\(\s*([^)]+?)\s*\)/g;
        const matches = [...value.matchAll(regex)];
        const fieldNames = matches.map(match => match[1].trim());

        // Return unique field names
        return [...new Set(fieldNames)];
    };

    const handleInputChange = (event, index) => {
        clearResults();
        const newFieldNamesWithValues = [...fieldNamesWithValues];
        newFieldNamesWithValues[index].value = event.target.value;
        setFieldNamesWithValues(newFieldNamesWithValues);
    };

    const clearResults = () => {
        if (evaluationResult) {
            setEvaluationResult({
                successfullyMapped: null,
                errorMessage: '',
                warningMessage: '',
                mappedValue: '',
                validationErrorMessage: '',
            });
        }
    };

    const getEvaluationResultText = () => {
        if (evaluationResult.successfullyMapped === null) {
            return '';
        }
        return evaluationResult.successfullyMapped ? 'Successful' : 'Failed';
    };

    return (
        <div className={`modal-body text-center ${style.modalBody}`}>
            <div className="row ing-text-heading3 text-start pt-3">
                <div className="col">
                    {getText('msg_bf233061a3b0')}
                    <Tooltip
                        html="These values should come from the file in the same format"
                        placement="right"
                    >
                        <i
                            className="bi bi-info-circle"
                            style={{
                                paddingLeft: '5px',
                                fontSize: '13px',
                                color: 'var(--iNGAGED-Primary-Color)',
                            }}
                        />
                    </Tooltip>
                </div>
            </div>
            <hr />
            {(fieldNamesWithValues || []).map((fieldNameWithValue, index) => (
                <div key={index} className="row pb-3">
                    <div className="col-3 text-start">
                        <label>{fieldNameWithValue.name}:</label>
                    </div>
                    <div className="col-9">
                        <input
                            type="text"
                            className="form-control"
                            value={fieldNameWithValue.value || ''}
                            onChange={event => handleInputChange(event, index)}
                        />
                    </div>
                </div>
            ))}

            <div className={`row justify-content-center `}>
                <div className="col-auto">
                    <button
                        type="button"
                        className="ing-button-primary-none"
                        onClick={testMapping}
                    >
                        {getText('msg_928ee083e40b')}
                    </button>
                </div>
            </div>

            {evaluationResult && (
                <>
                    <div className="row ing-text-heading3 text-start pt-3">
                        <div className="col">{getText('msg_612e12d29278')}</div>
                    </div>

                    <hr />

                    <div className="row ing-inputfield-name pb-3">
                        <div className="col-4 text-start">
                            <label>{getText('msg_013f0174cc9c')}</label>
                        </div>
                        <div className="col-8 text-start">
                            {getEvaluationResultText()}
                        </div>
                    </div>

                    {data.value && (
                        <div className="row ing-inputfield-name pb-3">
                            <div className="col-4 text-start">
                                <label>{getText('msg_411244b0a9a3')}</label>
                            </div>
                            <div className="col-8 text-start">
                                {evaluationResult?.mappedValue}
                            </div>
                        </div>
                    )}
                    {errorMessage && (
                        <div className="row ing-inputfield-name pb-3">
                            <div className="col-4 text-start">
                                <label>{getText('msg_547bc920a41d')}</label>
                            </div>
                            <div className="col-8 text-start">
                                {evaluationResult?.errorMessage}
                            </div>
                        </div>
                    )}
                    {warningMessage && (
                        <div className="row ing-inputfield-name pb-3">
                            <div className="col-4 text-start">
                                <label>{getText('msg_775d2573902a')}</label>
                            </div>
                            <div className="col-8 text-start">
                                {evaluationResult?.warningMessage}
                            </div>
                        </div>
                    )}

                    {evaluationResult.validationErrorMessage && (
                        <div className="row ing-inputfield-name pb-3">
                            <div className="col-4 text-start">
                                <label>{getText('msg_952dd6b72ab5')}</label>
                            </div>
                            <div className="col-8 text-start">
                                {evaluationResult?.validationErrorMessage}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ImportMapTest;
